// Дните зад ЕДИН ред на „По артикули" (и — със същата черупка — на „По
// категории").
//
// Таблицата показва целия период на един ред; въпросът „кой ден го вдигна"
// се чете само тук. За разлика от `StationDaysModal` данните НЕ идват от
// вече зареден отговор, а от отделна заявка
// (`GET /reports/analytics/ordered-items/by-day`) — затова модалът носи и
// състоянията „зарежда се" и „не се зареди".
//
// Компонентът е нарочно ОБЩ: не знае дали редът е артикул, добавка или
// категория. Заглавието, подзаглавието, иконата и самите дни идват отвън.

import type { ReactNode } from "react";
import { AppstoreOutlined, CloseOutlined } from "@ant-design/icons";
import { Modal, Spin } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { useViewport } from "../../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../../utils/modalStyles";
import { fmtEuro } from "../../../../utils/currency";
import { formatNumber, parseDecimal } from "../../../../components/dataTable";
import { pluralLabel } from "../reportPlural";
import type { OrderedItemsDay } from "../../../../api/reports";

type Props = {
  open: boolean;
  /** Името на реда, който е отворил разбивката. */
  title: string;
  /** Уточнението под него — станция, вид, категория… */
  subtitle?: string | null;
  /** Дните с продажби, възходящо по дата; дупките са дни без нито един ред. */
  days: OrderedItemsDay[];
  totalQuantity: number;
  grandTotal: number;
  isLoading: boolean;
  isError?: boolean;
  /** Иконата в хедъра — по подразбиране плочките на асортимента. */
  icon?: ReactNode;
  onClose: () => void;
};

/** Един показател под лентата на деня. */
const DayMetric = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-col gap-1 rounded-lg bg-canvas px-2.5 py-2">
    <span className="truncate text-[10px] font-bold uppercase leading-[1.2] tracking-[0.05em] text-muted">
      {label}
    </span>
    <span className="font-pos-mono text-[13px] font-bold leading-none tabular-nums text-ink">
      {value}
    </span>
  </div>
);

export const ItemDaysModal = ({
  open,
  title,
  subtitle,
  days,
  totalQuantity,
  grandTotal,
  isLoading,
  isError = false,
  icon,
  onClose,
}: Props) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();

  const width = isMobile ? undefined : isTablet ? 520 : 640;

  // Лентата на деня е спрямо НАЙ-СИЛНИЯ ден, не спрямо сбора: така се вижда
  // кой ден води, а не че всеки ден е малка част от периода.
  const strongestDay = days.reduce(
    (best, day) => Math.max(best, parseDecimal(day.total_sum)),
    0,
  );

  const daysLabel = pluralLabel(
    t,
    days.length,
    "reportsPage.itemDaysDayOne",
    "reportsPage.itemDaysDaysMany",
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="flex max-h-[82vh] flex-col bg-canvas">
        <header
          className={`flex shrink-0 items-start gap-3 border-b border-shade-2 bg-surface ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-5"
          }`}
        >
          <span className="grid size-10 shrink-0 place-items-center rounded-[10px] bg-brand-tint">
            {icon ?? (
              <AppstoreOutlined className="text-[20px] text-brand-deep-ink" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="m-0 truncate text-[17px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
              {title}
            </h2>
            <p className="mt-1 truncate text-[13px] font-medium leading-[1.4] text-soft">
              {subtitle || t("reportsPage.itemDaysSubtitle")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("reportsPage.itemDaysClose")}
            className="grid size-[30px] shrink-0 place-items-center rounded-lg text-muted transition-colors hover:bg-shade-1 hover:text-ink"
          >
            <CloseOutlined className="text-[14px]" />
          </button>
        </header>

        <div
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
            isNarrow ? "px-3 py-3" : "px-[18px] py-4"
          }`}
        >
          {isLoading ? (
            <div className="grid place-items-center py-14">
              <Spin size="large" />
            </div>
          ) : isError ? (
            <p className="py-10 text-center text-[13px] font-medium text-danger-ink">
              {t("reportsPage.itemDaysFailed")}
            </p>
          ) : days.length === 0 ? (
            <p className="py-10 text-center text-[13px] font-medium text-soft">
              {t("reportsPage.itemDaysEmpty")}
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {days.map((day) => {
                const dayRevenue = parseDecimal(day.total_sum);
                const sharePercent =
                  strongestDay > 0 ? (dayRevenue / strongestDay) * 100 : 0;
                const date = dayjs(day.date);
                return (
                  <article
                    key={day.date}
                    className={`flex flex-col gap-2.5 rounded-xl border border-shade-2 bg-surface ${
                      isNarrow ? "px-3.5 py-3" : "px-4 py-3.5"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2.5">
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
                          {/* Само името на деня минава през locale — самата
                              дата се пише ръчно, защото bg-BG лепи „ г." и
                              редът се пренася на два реда. */}
                          {date.toDate().toLocaleDateString(i18n.language, {
                            weekday: "long",
                          })}
                        </span>
                        <span className="mt-1 font-pos-mono text-[14px] font-bold leading-none tabular-nums text-ink">
                          {date.format("DD.MM.YYYY")}
                        </span>
                      </div>
                      <span className="font-pos-mono text-[17px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-ink">
                        {fmtEuro(dayRevenue)}
                      </span>
                    </div>

                    <div className="flex h-1.5 overflow-hidden rounded-full bg-fill">
                      {/* Делът спрямо най-силния ден е известен чак по време на
                          изпълнение — само ширината идва от данните. */}
                      <span
                        className="rounded-full bg-brand"
                        style={{ width: `${sharePercent}%` }}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <DayMetric
                        label={t("reportsPage.itemDaysQuantity")}
                        value={formatNumber(parseDecimal(day.count))}
                      />
                      <DayMetric
                        label={t("reportsPage.itemDaysAvgPrice")}
                        value={fmtEuro(parseDecimal(day.avg_price))}
                      />
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>

        <footer
          className={`flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-shade-2 bg-surface ${
            isNarrow ? "px-4 py-3.5" : "px-6 py-4"
          }`}
        >
          <div className="flex min-w-0 flex-wrap items-center gap-x-3.5 gap-y-1.5">
            <span className="font-pos-mono text-[10px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
              {t("reportsPage.total")}
            </span>
            <span className="text-[11px] font-medium text-soft">
              {daysLabel}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="text-[11px] font-medium text-soft">
                {t("reportsPage.itemDaysQuantity")}
              </span>
              <span className="font-pos-mono text-[12px] font-bold tabular-nums text-ink">
                {formatNumber(totalQuantity)}
              </span>
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="font-pos-mono text-[18px] font-extrabold leading-none tracking-[-0.02em] tabular-nums text-ink">
              {fmtEuro(grandTotal)}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="h-10 rounded-[10px] border border-shade-3 bg-surface px-4 text-[13px] font-bold text-soft transition-colors hover:bg-fill"
            >
              {t("reportsPage.itemDaysClose")}
            </button>
          </div>
        </footer>
      </div>
    </Modal>
  );
};
